import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ALLOWED_ORIGINS,
  enqueuePendingCampaignAdd,
  nextAddRequestToken,
  normalizeCampaignParams,
  parseCampaignId,
  persistUsr,
  resolveAndRoute,
  setParentOrigin,
} from '../../utils/parentBridge.js';

/**
 * App-wide listener for parent (Service Intel) postMessages. Unlike the old
 * <Embed>-only listener, this is mounted for the whole app lifetime, so it
 * keeps reacting to changes the parent sends AFTER the first load — e.g. the
 * parent switching nacCampaignId, or sending 0 to start a NEW campaign.
 *
 * Renders nothing. It only navigates when the campaign/shop actually changes
 * (deduped in resolveAndRoute), so it never fights normal in-app navigation.
 */
export const ParentBridge = () => {
  const navigate = useNavigate();

  useEffect(() => {
    const handleMessage = (event) => {
      if (ALLOWED_ORIGINS.indexOf(event.origin) === -1) {
        // Surface SI-looking messages that get dropped for being off-allowlist —
        // a very common "nothing happens" cause. Ignore unrelated noise.
        const d = event.data;
        const looksLikeSi =
          d && typeof d === 'object' &&
          (d.type === 'usr' ||
            d.data?.CampaignId != null ||
            d.data?.AppHost != null ||
            d.AppHost != null);
        if (looksLikeSi) {
          console.warn(
            '[postcards/bridge] message DROPPED — origin not in allowlist',
            { origin: event.origin, allowed: ALLOWED_ORIGINS, type: d?.type }
          );
        }
        return;
      }
      if (!event.data) return;

      // Raw view of every accepted parent message (origin already validated).
      console.log('[postcards/bridge] message received', {
        origin: event.origin,
        type: event.data?.type,
        dataKeys:
          event.data?.data && typeof event.data.data === 'object'
            ? Object.keys(event.data.data)
            : null,
      });

      // Remember where the parent lives so we can post results back to it.
      setParentOrigin(event.origin);

      // Live radius edit from the parent (see postmessage-radius.md). We
      // validate origin + range here and re-broadcast it as an internal
      // CustomEvent so whichever map page is mounted (ShopDetail or CampaignMap,
      // including view=campaign) can PATCH the radius and redraw its coverage
      // circle. Centralizing the origin allowlist here means the page handlers
      // don't each need to re-validate it.
      if (event.data.type === 'radius-update') {
        const radiusMiles = Number(event.data?.data?.radiusMiles);
        console.log('[postcards/bridge] radius-update message', { radiusMiles });
        if (Number.isFinite(radiusMiles) && radiusMiles >= 0.1 && radiusMiles <= 50) {
          window.dispatchEvent(
            new CustomEvent('postcards:radius-update', { detail: { radiusMiles } })
          );
        } else {
          console.warn(
            '[postcards/bridge] radius-update ignored — out of range [0.1, 50]',
            radiusMiles
          );
        }
        return;
      }

      // Live show/hide of existing-customer markers, controlled by the parent.
      // Same bridge as radius-update: validate origin (above) + re-broadcast as
      // an internal CustomEvent so the mounted map page (ShopDetail or
      // CampaignMap) toggles the markers instantly. Accepts `show` / `visible` /
      // `showExistingCustomers` for robustness.
      if (event.data.type === 'existing-customers') {
        const d = event.data?.data || {};
        const raw = d.show ?? d.visible ?? d.showExistingCustomers;
        const show = !(raw === false || raw === 'false' || raw === 0 || raw === '0');
        console.log('[postcards/bridge] existing-customers message', { show });
        window.dispatchEvent(
          new CustomEvent('postcards:existing-customers', { detail: { show } })
        );
        return;
      }

      // Center the map on (and highlight) a specific point chosen by SI. We
      // accept melissaAddressKey (exact match → full popup) and/or lat/lng
      // (fallback center). Re-broadcast for the mounted map page.
      if (event.data.type === 'focus-address') {
        const d = event.data?.data || {};
        const detail = {
          melissaAddressKey: d.melissaAddressKey ?? d.addressKey ?? null,
          latitude: d.latitude != null ? Number(d.latitude) : null,
          longitude: d.longitude != null ? Number(d.longitude) : null,
        };
        console.log('[postcards/bridge] focus-address message', detail);
        window.dispatchEvent(
          new CustomEvent('postcards:focus-address', { detail })
        );
        return;
      }

      if (event.data.type !== 'usr') return;

      const usrData = event.data.data;
      console.log('[postcards/bridge] usr message', {
        UserId: usrData?.UserId,
        lid: usrData?.lid,
        nacCampaignId:
          usrData?.CampaignId ?? usrData?.NacCampaignId ?? usrData?.nacCampaignId,
        AppHost: usrData?.AppHost,
      });

      persistUsr(usrData);
      resolveAndRoute(usrData, navigate);

      // Add-to-campaign trigger: SI re-sends the `usr` payload with a
      // `campaignParams` object (alongside view:'campaign' + CampaignId > 0) to
      // run a search and APPEND its NEW addresses to that campaign. We normalize
      // the params, stash the request (CampaignMap may not be mounted yet on the
      // very first message — it's about to be navigated to), and broadcast a
      // CustomEvent so an already-mounted CampaignMap reacts immediately. A
      // per-request token makes it run exactly once. See ADD_TO_CAMPAIGN_API.md §6.
      const campaignId = parseCampaignId(
        usrData?.CampaignId ?? usrData?.NacCampaignId ?? usrData?.nacCampaignId
      );
      if (campaignId && usrData?.campaignParams) {
        const detail = {
          campaignId,
          token: nextAddRequestToken(),
          // Pass AppHost straight through from the message so the add request
          // never depends on localStorage having been written first (and works
          // even if persistUsr was a no-op). It's required for the NCA push.
          appHost: usrData.AppHost || usrData.appHost || '',
          params: normalizeCampaignParams(usrData.campaignParams),
        };
        console.log('[postcards/bridge] add-to-campaign request', detail);
        enqueuePendingCampaignAdd(detail);
        window.dispatchEvent(
          new CustomEvent('postcards:add-to-campaign', { detail })
        );
      }
    };

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [navigate]);

  return null;
};

export default ParentBridge;
